import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaCalendar, FaTag, FaUser, FaArrowRight, FaSearch, FaRss, FaTerminal } from 'react-icons/fa';
import { allCommands, getCommandsByCategory } from '../data/cyberCommands';
import { CommandTicker } from '../components/CommandStream';

const API_URL = 'https://arap-backend.onrender.com/api/blog';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  const bgCommands = useMemo(() => [...allCommands].sort(() => Math.random() - 0.5), []);
  const osintCmds = useMemo(() => getCommandsByCategory('osint').slice(0, 6), []);
  const siemCmds = useMemo(() => getCommandsByCategory('siem').slice(0, 6), []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(API_URL);
        setPosts(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Failed to load blog posts.');
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const allTags = [...new Set(posts.flatMap((p) => p.tags))].sort();

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = !selectedTag || post.tags.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="relative pt-20 pb-20 min-h-screen">
      {/* Background command waterfall */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden command-fade-top z-0">
        <div className="flex h-full opacity-[0.02]">
          {[0, 1].map((col) => (
            <div key={col} className="flex-1 overflow-hidden">
              <div className="font-mono text-[9px] text-cyber-green leading-loose" style={{ animation: `scrollUp ${60 + col * 25}s linear infinite`, paddingTop: `${col * 350}px` }}>
                {[...bgCommands, ...bgCommands].map((cmd, i) => (
                  <div key={i} className="truncate px-3">$ {cmd}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FaRss className="text-cyber-green text-2xl" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Security <span className="text-cyber-green">Dispatches</span>
            </h1>
          </div>
          <p className="text-cyber-muted text-sm font-mono">{'>'} Threat intelligence, research findings, and cybersecurity insights.</p>
          <div className="w-20 h-px bg-cyber-green mt-4"></div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Search */}
            <div className="relative mb-8">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-cyber-muted" />
              <input type="text" placeholder="Search dispatches..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-cyber-dark border border-cyber-green/20 rounded-lg text-cyber-text font-mono text-sm placeholder-cyber-muted" />
            </div>

            {loading && (
              <div className="text-center py-20">
                <FaTerminal className="text-4xl text-cyber-green mx-auto mb-4 animate-pulse" />
                <p className="text-cyber-green font-mono animate-pulse">{'>'} Loading dispatches...</p>
              </div>
            )}

            {error && (
              <div className="p-4 border border-cyber-red/30 rounded-lg bg-cyber-red/10 mb-6">
                <p className="text-cyber-red text-sm font-mono">[ERROR] {error}</p>
              </div>
            )}

            {!loading && !error && filteredPosts.length === 0 && (
              <div className="text-center py-20">
                <div className="terminal-window max-w-md mx-auto">
                  <div className="terminal-header">
                    <div className="terminal-dot bg-cyber-red"></div>
                    <div className="terminal-dot bg-cyber-amber"></div>
                    <div className="terminal-dot bg-cyber-green"></div>
                    <span className="ml-3 text-xs text-cyber-muted">blog.sh</span>
                  </div>
                  <div className="terminal-body text-center py-8">
                    <p className="text-cyber-muted mb-2">
                      {searchTerm || selectedTag ? '> No dispatches match your query.' : '> No dispatches published yet.'}
                    </p>
                    <p className="text-cyber-green text-sm">Stay tuned for incoming transmissions.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Blog Posts */}
            <div className="space-y-6">
              {filteredPosts.map((post, i) => (
                <motion.div key={post._id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                  <Link to={`/blog/${post.slug}`} className="block group">
                    <div className="cyber-card overflow-hidden">
                      {post.coverImage && (
                        <div className="h-48 md:h-56 overflow-hidden">
                          <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-cyber-muted mb-3">
                          <span className="flex items-center gap-1"><FaCalendar className="text-cyber-green" />{formatDate(post.createdAt)}</span>
                          <span className="flex items-center gap-1"><FaUser className="text-cyber-green" />{post.author}</span>
                        </div>
                        <h2 className="text-xl font-bold text-white group-hover:text-cyber-green transition-colors mb-3">{post.title}</h2>
                        <p className="text-sm text-cyber-muted leading-relaxed mb-4">{post.excerpt}</p>
                        {post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.tags.map((tag) => (
                              <span key={tag} className="text-xs px-2 py-1 rounded bg-cyber-green/10 text-cyber-green border border-cyber-green/20">{tag}</span>
                            ))}
                          </div>
                        )}
                        <span className="inline-flex items-center gap-2 text-sm text-cyber-cyan group-hover:text-cyber-green transition-colors">
                          Read more <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-72 flex-shrink-0 space-y-6">
            {/* Tags */}
            {allTags.length > 0 && (
              <div className="terminal-window">
                <div className="terminal-header">
                  <div className="terminal-dot bg-cyber-red"></div>
                  <div className="terminal-dot bg-cyber-amber"></div>
                  <div className="terminal-dot bg-cyber-green"></div>
                  <span className="ml-3 text-xs text-cyber-muted">tags</span>
                </div>
                <div className="terminal-body">
                  <div className="flex items-center gap-2 mb-3"><FaTag className="text-cyber-green text-sm" /><span className="text-sm text-cyber-green font-semibold">Filter by Tag</span></div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setSelectedTag('')} className={`text-xs px-3 py-1 rounded border transition-colors ${!selectedTag ? 'bg-cyber-green text-cyber-black border-cyber-green' : 'border-cyber-green/20 text-cyber-muted hover:text-cyber-green hover:border-cyber-green/40'}`}>All</button>
                    {allTags.map((tag) => (
                      <button key={tag} onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)} className={`text-xs px-3 py-1 rounded border transition-colors ${tag === selectedTag ? 'bg-cyber-green text-cyber-black border-cyber-green' : 'border-cyber-green/20 text-cyber-muted hover:text-cyber-green hover:border-cyber-green/40'}`}>{tag}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="terminal-window">
              <div className="terminal-header">
                <div className="terminal-dot bg-cyber-red"></div>
                <div className="terminal-dot bg-cyber-amber"></div>
                <div className="terminal-dot bg-cyber-green"></div>
                <span className="ml-3 text-xs text-cyber-muted">stats</span>
              </div>
              <div className="terminal-body text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-cyber-muted">Total Posts:</span><span className="text-cyber-green">{posts.length}</span></div>
                  <div className="flex justify-between"><span className="text-cyber-muted">Tags:</span><span className="text-cyber-green">{allTags.length}</span></div>
                  <div className="flex justify-between"><span className="text-cyber-muted">Status:</span><span className="text-cyber-green">● Active</span></div>
                </div>
              </div>
            </div>

            {/* OSINT Commands */}
            <div className="terminal-window">
              <div className="terminal-header py-1.5 px-3">
                <div className="terminal-dot bg-cyber-red" style={{ width: 8, height: 8 }}></div>
                <div className="terminal-dot bg-cyber-amber" style={{ width: 8, height: 8 }}></div>
                <div className="terminal-dot bg-cyber-green" style={{ width: 8, height: 8 }}></div>
                <span className="ml-2 text-[10px] text-cyber-muted">intel_gathering.sh</span>
              </div>
              <div className="p-2.5 text-[10px] leading-relaxed bg-cyber-black/40 font-mono">
                <div className="text-cyber-cyan/50 mb-1">{'// osint reconnaissance'}</div>
                {osintCmds.map((cmd, i) => (
                  <div key={i} className="text-cyber-green/50 truncate"><span className="text-cyber-cyan/30">$</span> {cmd}</div>
                ))}
                <div className="text-cyber-cyan/50 mt-2 mb-1">{'// siem analysis'}</div>
                {siemCmds.map((cmd, i) => (
                  <div key={i} className="text-cyber-green/50 truncate"><span className="text-cyber-cyan/30">$</span> {cmd}</div>
                ))}
              </div>
            </div>

            {/* Admin Link */}
            <Link to="/blog/admin" className="block text-center text-xs text-cyber-muted hover:text-cyber-green transition-colors border border-cyber-green/10 hover:border-cyber-green/30 rounded-lg p-3 font-mono">
              <FaTerminal className="inline mr-2" />Admin Panel
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="mt-16 py-2 border-t border-cyber-green/10 bg-cyber-dark/30 overflow-hidden command-fade-sides relative z-10">
        <CommandTicker speed={75} />
      </div>
    </div>
  );
};

export default Blog;
